<template>
  <div
    v-if="filteringCategories"
    class="tw:flex tw:mx-auto tw:flex-col tw:w-full tw:md:max-w-screen-lg tw:lg:max-w-screen-xl tw:py-6"
  >
    <div v-if="type === 'firstVisit'">
      <p>{{ $t('hotel_offers_subtext') }}</p>
    </div>
    <div v-if="type === 'create'" class="tw:flex tw:w-full tw:justify-between tw:items-center tw:mt-2">
      <div>
        <p class="tw:text-2xl tw:font-bold tw:mb-1">{{ $t('hotel_offers_product_header') }}</p>
        <p>{{ $t('hotel_offers_header_subtext') }}</p>
      </div>
      <MainButton size="big" @click="openCreateOfferOpen({ offerName: null, source: 'button' })">{{
        $t('hotel_offers_cta_post')
      }}</MainButton>
    </div>
    <div v-if="type === 'explore'">
      <p class="tw:text-2xl tw:font-bold tw:mb-1">{{ $t('hotel_offers_header') }}</p>
      <p>{{ $t('hotel_offers_header_subtext') }}</p>
    </div>

    <HotelOffersFilter
      :filteringCategories="filteringCategories"
      :selectedFilter="selectedFilter"
      @selectFilter="selectFilter"
      dataTestIdLocation="grid"
    />
    <HotelOffersPackagesBanner
      v-if="selectedFilter === 'all'"
      @newOffersClicked="() => emit('selectFilter', 'package')"
      @openCreateOfferOpen="openCreateOfferOpen"
    />
    <transition-group
      name="list"
      tag="div"
      class="tw:mb-6 tw:grid tw:grid-cols-[1fr] tw:md:grid-cols-[1fr_1fr] tw:lg:grid-cols-[1fr_1fr_1fr] tw:gap-4"
    >
      <div
        v-for="template in filteredTemplates.slice(0, itemsToShow)"
        :key="template.name"
        class="tw:transition-all tw:duration-500 tw:flex tw:flex-col"
      >
        <HotelOfferItem
          :name="template.name"
          :category="template.category"
          :icon="icons[template.name]"
          :status="template.status"
          @openCreateOfferOpen="openCreateOfferOpen"
        />
      </div>
    </transition-group>
    <button
      class="tw:flex tw:mb-6 tw:text-sm tw:text-blue-700 tw:font-bold tw:disabled:text-gray-300"
      @click="showMoreItems"
      v-if="type === 'explore' && filteredTemplates.length > itemsToShow"
      :disabled="itemsToShow === filteredTemplates.length"
    >
      {{ $t('hotel_offers_show') }}
    </button>
    <div class="tw:flex tw:flex-row tw:mb-11">
      <p class="tw:text-sm">{{ $t('hotel_offers_not_found') }}</p>
      <button class="tw:ml-1 tw:text-sm tw:text-blue-700 tw:font-bold" @click="openSuggestionsModal">
        {{ $t('hotel_offers_cta_ideas') }}
      </button>
    </div>
  </div>
</template>
<script setup>
import { computed, ref, watch } from 'vue'

import HotelOfferItem from '@/components/hotelOffers/HotelOfferItem.vue'
import HotelOffersFilter from '@/components/hotelOffers/HotelOffersFilter.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'

import { selectedAccommodationId } from '@/layouts/queries'

import appAnalytics from '@/utils/tracking'
import HotelOffersPackagesBanner from '@/components/hotelOffers/HotelOffersPackagesBanner.vue'

const emit = defineEmits(['selectFilter', 'openSuggestionsModal', 'openCreateOfferOpen'])

const selectFilter = value => {
  emit('selectFilter', value)
  if (props.type === 'explore') itemsToShow.value = 6
  else itemsToShow.value = props.filteredTemplates.length
}
const openSuggestionsModal = source => emit('openSuggestionsModal', { offerName: null, source })
const openCreateOfferOpen = data => {
  appAnalytics.track(appAnalytics.events.HO_OFFER_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    selectedOffer: data.offerName,
    source: 'Offers Grid',
  })
  emit('openCreateOfferOpen', data)
}
const props = defineProps({
  type: String,
  filteredTemplates: {
    type: Array,
    default: () => [],
  },
  filteringCategories: {
    type: Array,
    default: () => [],
  },
  selectedFilter: {
    type: String,
    default: 'all',
  },
  icons: Object,
})

const visibleItems = computed(() => {
  if (props.type === 'explore') return 6
  else return props.filteredTemplates.length
})

const itemsToShow = ref(visibleItems.value)

watch(visibleItems, newVal => {
  if (itemsToShow.value !== newVal) {
    itemsToShow.value = newVal
  }
})

const showMoreItems = () => {
  itemsToShow.value = props.filteredTemplates.length
}
</script>
<style scoped>
.list-enter-active,
.list-leave-active {
  transition:
    transform 0.5s,
    opacity 0.5s;
}
.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
.list-enter-to,
.list-leave {
  opacity: 1;
  transform: translateX(0);
}
</style>
